import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";

import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SubdirectoryArrowLeftIcon from "@mui/icons-material/SubdirectoryArrowLeft";
import {
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  Input,
  Accordion as MuiAccordion,
  Stack,
  styled,
  Typography
} from "@mui/material";
import { PsychologyReverse, PsychologyReverseWithCircle } from "assets/material-custom-icons";
import { DealRoleGuard } from "components/guards";
import { DocumentAnswerState, DocumentSectionType } from "constants/documents";
import { useActionHook, useDidUpdate, useModals, useRoleGuardHook } from "hooks";
import { debounce } from "lodash";
import {
  IApiServiceError,
  // useDocumentAnswersSelect,
  // useDocumentSectionSelect,
  useDocumentsActions,
  useSelectedQuestionSelect,
  useViewActions
} from "store";
import { DocumentAnswer as DocumentAnswerType, DocumentQuestion, DocumentSection, QuestionAiStatus } from "store/types";

import { RoleName } from "../../../constants";
import { DocumentAnswer } from "../Answers/DocumentAnswer";
import { useContentContext } from "../Content/content-context";

interface Props {
  question: DocumentQuestion;
  documentId: number;
  handleOpenMenu: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, questionId: number) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  disableBM: boolean;
  createQuestionAndFocus: () => void;
  questionsLength: number;
  setPendingAIQuestionIds: Dispatch<SetStateAction<number[]>>;
  pendingAIQuestionIds: number[];
  disable: boolean; //flag of permission restriction
  section: DocumentSection;
}

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  () => ({
    marginBottom: "10px",
    "&:not(:last-child)": {
      borderBottom: 0
    },
    "&:before": {
      display: "none"
    }
  })
);

export const Question: React.FC<Props> = ({
  question,
  documentId,
  inputRef,
  handleOpenMenu,
  disableBM,
  createQuestionAndFocus,
  questionsLength,
  setPendingAIQuestionIds,
  pendingAIQuestionIds,
  disable = false,
  section
}) => {
  const { getAnswers, updateSection, updateQuestion, createAnswer, askAIAnswer } = useContentContext();
  const { id, question: questionValueFromStore, section_id } = question;
  const answers = getAnswers({ question_id: id, parent_answer_id: null });
  const sortedParentAnswers = useMemo<DocumentAnswerType[]>(() => {
    return answers.sort((a, b) => {
      if (a.state === DocumentAnswerState.Draft) return 1;
      if (b.state === DocumentAnswerState.Draft) return -1;
      if (a.published_at && b.published_at) {
        return new Date(a.published_at).getTime() - new Date(b.published_at).getTime();
      }
      return 0;
    });
  }, [answers]);

  const enabledOption = useRoleGuardHook(true, [RoleName.Admin, RoleName.Analyst, RoleName.Guest]);
  const [isQuestionOpen, setIsQuestionOpen] = useState<boolean>(false);
  const [questionValue, setQuestionValue] = useState<string>(questionValueFromStore);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

  const askAiLoading = useMemo(() => pendingAIQuestionIds.includes(id), [pendingAIQuestionIds, id]);

  const { createDocumentAiAnswerAction } = useDocumentsActions();
  const selectedQuestion = useSelectedQuestionSelect();

  const { setSelectedQuestionAction } = useViewActions();
  const { errorModal } = useModals();

  const { action: updateDocumentQuestion, loading: updateLoading, setLoading } = useActionHook(updateQuestion);

  const questionInputEnabled = useRoleGuardHook(true, [RoleName.Admin, RoleName.Analyst]);
  const updateDocumentQuestionDeb = useMemo(() => debounce(updateDocumentQuestion, 1000), []);

  const handleAskAi = useCallback(
    async (e: React.MouseEvent, questionId: number) => {
      try {
        e.stopPropagation();
        if (section?.type === DocumentSectionType.AIFile) {
          await createDocumentAiAnswerAction(questionId);
        } else {
          await askAIAnswer({ question_id: questionId, documentId });
        }
      } catch (e) {
        errorModal({ msg: (e as IApiServiceError).message });
      }
    },
    [answers, documentId, section, askAiLoading]
  );

  const handleEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      createQuestionAndFocus();
    }
  };

  const handleAddAnswer = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (askAiLoading) return;
      setIsQuestionOpen(true);
      setSelectedQuestionAction(question);
      const unPublishedAnswer = answers.find((item) => !item.published_at);
      if (unPublishedAnswer) return;
      if (section?.type === DocumentSectionType.AIFile) return;

      createAnswer({ documentId, question_id: id, answer: { text: "" } });
    },
    [askAiLoading, question, answers, section, documentId, id, createAnswer, setSelectedQuestionAction]
  );

  useDidUpdate(() => {
    if (updateLoading) return;
    if (isInputFocused) return;
    if (questionValue === questionValueFromStore) return;
    setQuestionValue(questionValueFromStore);
  }, [questionValueFromStore]);

  const aiButtonEnabled = useMemo(() => {
    if (
      section?.type !== DocumentSectionType.AIFile &&
      section?.type !== DocumentSectionType.Question &&
      section?.type !== DocumentSectionType.AIQuestion
    )
      return false;
    if (askAiLoading || section?.ai_status === QuestionAiStatus.InProgress) return false;
    return true;
  }, [section, askAiLoading, enabledOption, question]);

  const isInputDisabled = useMemo(() => {
    if (askAiLoading) {
      return true;
    } else if (section?.ai_status === QuestionAiStatus.InProgress) {
      return true;
    }
    return false;
  }, [answers.length, askAiLoading, section]);

  const isMenuAvailableForQuestion = useMemo(() => {
    if (askAiLoading) return false;
    if (questionsLength === 1) return false;
    if (section?.ai_status === QuestionAiStatus.InProgress) return false;
    return true;
  }, [questionsLength, section, askAiLoading]);

  const isAnswerLoading = section?.ai_status === QuestionAiStatus.InProgress || askAiLoading;

  // Answers actually rendered in the details (a draft only shows for the selected question)
  const visibleAnswers = useMemo(
    () =>
      sortedParentAnswers.filter(
        (answer) => !(answer.question_id !== selectedQuestion?.id && answer.state === DocumentAnswerState.Draft)
      ),
    [sortedParentAnswers, selectedQuestion]
  );

  // Avoid rendering empty AccordionDetails (its padding creates blank space) when there is nothing to show
  const hasDetails = isAnswerLoading || visibleAnswers.length > 0;

  return (
    <>
      <Accordion
        key={id}
        expanded={isQuestionOpen}
        {...(disableBM && { sx: { mb: 0 } })}
        onChange={(event: React.SyntheticEvent) => {
          //need for avoid opening question when press on "space" native accordion issues
          if ((event.nativeEvent as PointerEvent).pointerType === "") {
            return;
          }
          setIsQuestionOpen((prev) => !prev);
        }}
        TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
      >
        <AccordionSummary
          sx={{
            display: "flex",
            minHeight: "unset",
            padding: "0 10px 0 20px",
            bgcolor: "#edf1f9",
            borderRadius: "5px",
            border: "1px solid #dae0e2",
            "&.Mui-focusVisible, &.Mui-expanded, &:hover, &:focus": { bgcolor: "#edf1f9" },
            ".MuiAccordionSummary-content": { margin: 0, alignItems: "center", gap: "10px" },
            ":hover .question-add-answer": { opacity: 1 }
          }}
        >
          <FiberManualRecordIcon sx={{ fontSize: "5px" }} />
          <Box sx={{ flexGrow: 1, position: "relative" }} dir="auto">
            {isInputDisabled && <Box sx={{ position: "absolute", width: "100%", height: "100%", zIndex: 1 }} />}
            <Input
              sx={{
                fontSize: 14,
                color: "#0a3142",
                width: "100%",
                ".Mui-disabled": { "-webkit-text-fill-color": "#0a3142" }
              }}
              onFocus={() => {
                if (askAiLoading) return;
                setIsInputFocused(true);
              }}
              onBlur={() => setIsInputFocused(false)}
              onKeyDown={handleEnterKeyDown}
              onPaste={(e) => {
                e.stopPropagation();
              }}
              inputRef={inputRef}
              placeholder="Start writing a question"
              value={questionValue}
              disabled={isInputDisabled || disable}
              readOnly={!questionInputEnabled}
              disableUnderline
              onChange={(e) => {
                setQuestionValue(e.target.value);
                updateDocumentQuestionDeb({ documentId, question: e.target.value, id: id });
                if (!updateLoading) setLoading(true);
              }}
              inputProps={{
                dir: "auto"
              }}
            />
          </Box>
          <Stack direction="row" sx={{ alignItems: "center" }}>
            {enabledOption && !disable && !askAiLoading && (
              <IconButton
                className="question-add-answer"
                onClick={handleAddAnswer}
                sx={{
                  padding: 0,
                  width: "16px",
                  height: "16px",
                  marginRight: "10px",
                  opacity: 0,
                  transition: "opacity 0.2s linear"
                }}
              >
                <SubdirectoryArrowLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {askAiLoading && <CircularProgress size={17} />}
            <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              {isMenuAvailableForQuestion && (
                <IconButton sx={{ padding: 0, width: "20px", height: "20px" }} onClick={(e) => handleOpenMenu(e, id)}>
                  <MoreHorizIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}

              {aiButtonEnabled && !disable && (
                <IconButton
                  onClick={async (e) => {
                    e.stopPropagation();
                    setPendingAIQuestionIds((prev) => [...prev, id]);
                    setIsInputFocused(false);
                    setSelectedQuestionAction(null);
                    section?.type === DocumentSectionType.Question &&
                      (await updateSection({ id: section_id, type: DocumentSectionType.AIQuestion }));
                    await handleAskAi(e, id);

                    setPendingAIQuestionIds((prev) => prev.filter((item) => item !== id));
                  }}
                  sx={{ padding: 0, width: "20px", height: "20px" }}
                >
                  <PsychologyReverse sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </DealRoleGuard>
          </Stack>
        </AccordionSummary>
        {hasDetails && (
          <AccordionDetails className="documents__question-details">
            {isAnswerLoading ? (
              <Stack
                alignItems="center"
                gap="6px"
                sx={{
                  width: "100%",
                  backgroundColor: "#F6F8FC",
                  borderRadius: "4px",
                  marginTop: "10px",
                  padding: "12px"
                }}
              >
                <PsychologyReverseWithCircle sx={{ fontSize: "30px" }} />
                <Typography sx={{ fontSize: "16px", color: "#92A7BC" }}>Generating the answer...</Typography>
              </Stack>
            ) : (
              visibleAnswers.map((answer) => (
                <DocumentAnswer key={answer.id} documentId={documentId} question={question} answer={answer} />
              ))
            )}
          </AccordionDetails>
        )}
      </Accordion>
    </>
  );
};
